import { useEffect, useState } from "react";
import {
  Button,
  Confirm,
  useRecordContext,
  useDataProvider,
  useNotify,
  useRefresh,
  useTranslate,
} from "react-admin";
import { useMutation, useQueryClient } from "react-query";
import { getReadableErrorMessage } from "./errorMessage";

const ConfirmBtn = ({ title, content, resource, dataBody, confirmProps = {}, invalidateQueries = [], ...props }) => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const queryClient = useQueryClient();

  const { mutate, isLoading, error, isSuccess } = useMutation(() => {
    return dataProvider.update(resource, {
      id: record.id,
      data: dataBody ? dataBody : {},
    });
  });

  useEffect(() => {
    if (error) {
      const readableErrorMessage = getReadableErrorMessage(error);

      if (readableErrorMessage === "403") {
        notify("mine.notification.403_error", { type: "error" });
        return;
      }

      if (
        readableErrorMessage.startsWith("server") ||
        readableErrorMessage.startsWith("ra.notification")
      ) {
        notify(readableErrorMessage, {
          type: "error",
        });
      } else {
        if (readableErrorMessage.includes("*")) {
          const messArg = readableErrorMessage.split("*");
          notify("server." + messArg[0].split(" ").join("_"), {
            messageArgs: { code: messArg[1] },
            type: "error",
          });
        } else {
          notify(readableErrorMessage, { type: "error" });
        }
      }
    }
  }, [error]);
  useEffect(() => {
    if (isSuccess) {
      refresh();
      // Remove cache for specified queries to force fresh data on next open
      if (invalidateQueries.length > 0) {
        invalidateQueries.forEach((queryKey) => {
          // Remove all cached queries that contain the specified resource name
          queryClient.removeQueries({
            predicate: (query) => {
              const key = query.queryKey;
              if (Array.isArray(key)) {
                return key.some((k) => typeof k === 'string' && k.includes(queryKey));
              }
              return typeof key === 'string' && key.includes(queryKey);
            },
          });
        });
      }
      notify("ra.notification.updated", {
        type: "info",
        messageArgs: { smart_count: 1 },
      });
    }
  }, [isSuccess]);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };
  const handleDialogClose = () => setOpen(false);
  const handleConfirm = () => {
    mutate();
    setOpen(false);
  };

  return (
    <>
      <Button onClick={handleClick} disabled={isLoading} {...props} />
      <Confirm
        isOpen={open}
        loading={isLoading}
        title={translate(title)}
        content={content}
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
        {...confirmProps}
      />
    </>
  );
};

export default ConfirmBtn;
